'use client';

import { useEffect, useState, type ComponentPropsWithRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Icon as IconUI } from '@/shared-ui/atoms/icon';
import { ProgressBar } from '@/shared-ui/atoms/progress-bar';
import { Typography } from '@/shared-ui/atoms/typography';
import { HeartIcon } from '@/shared-ui/icons/heart';
import { InfoIcon } from '@/shared-ui/icons/info';
import { Tooltip } from '@/shared-ui/molecules/tooltip';

// We allow values outside the 0-100 range to be passed in, but we'll clamp them to 0-88 for the heart position to prevent it from overflowing the bar container.
// The ProgressBar itself will still fill according to the actual value (up to 100%).
const clampProgress = (value: number) => Math.max(0, Math.min(88, Number.isFinite(value) ? value : 0));

type RootProps = ComponentPropsWithRef<'div'>;

const Root = ({ className, ref, ...props }: RootProps) => (
    <div data-slot="matches-progress" ref={ref} className={cn('flex items-center gap-2', className)} {...props} />
);
Root.displayName = 'MatchesProgress.Root';

type ProgressProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    value: number;
};

const Progress = ({ value, className, ref, ...props }: ProgressProps) => {
    const [displayValue, setDisplayValue] = useState(0);
    const [isBouncing, setIsBouncing] = useState(false);

    useEffect(() => {
        setIsBouncing(false);
        const id = requestAnimationFrame(() => setDisplayValue(value));
        return () => cancelAnimationFrame(id);
    }, [value]);

    const progress = clampProgress(displayValue);
    const animationDuration = 'duration-[1000ms]';

    return (
        <div data-slot="matches-progress-bar" ref={ref} className={cn('relative w-12', className)} {...props}>
            <ProgressBar variant="matches" value={clampProgress(displayValue)} animationDuration={animationDuration} />
            <IconUI
                icon={HeartIcon}
                fill="currentColor"
                decorative
                data-slot="matches-progress-heart"
                className={cn(
                    'text-snk-matches-pink pointer-events-none absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 transition-[left] ease-out motion-reduce:transition-none',
                    animationDuration,
                    isBouncing && 'animate-[matchesHeartBounce_1000ms_ease-out_2] motion-reduce:animate-none'
                )}
                onTransitionEnd={(event) => {
                    if (event.propertyName !== 'left') return;
                    setIsBouncing(false);
                    requestAnimationFrame(() => setIsBouncing(true));
                }}
                onAnimationEnd={() => setIsBouncing(false)}
                style={{ left: `${progress}%` }}
            />
        </div>
    );
};
Progress.displayName = 'MatchesProgress.Progress';

type TextProps = ComponentPropsWithRef<'p'>;

const Text = ({ className, ref, ...props }: TextProps) => (
    <Typography
        data-slot="matches-progress-label"
        variant="small"
        weight="regular"
        ref={ref as never}
        className={cn(className)}
        {...props}
    />
);
Text.displayName = 'MatchesProgress.Text';

type MatchesIconProps = Omit<ComponentPropsWithRef<'button'>, 'type' | 'aria-label'> & {
    children: ReactNode;
    ariaLabel?: string;
};

const Icon = ({ children, className, ref, ariaLabel = 'More info', ...props }: MatchesIconProps) => {
    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <button
                    {...props}
                    type="button"
                    aria-label={ariaLabel}
                    data-slot="matches-progress-info"
                    ref={ref}
                    className={cn('shrink-0', className)}>
                    <IconUI icon={InfoIcon} decorative className="size-4" />
                </button>
            </Tooltip.Trigger>
            <Tooltip.Content>
                <Tooltip.Body>{children}</Tooltip.Body>
            </Tooltip.Content>
        </Tooltip.Root>
    );
};
Icon.displayName = 'MatchesProgress.Icon';

const MatchesProgress = {
    Root,
    Progress,
    Text,
    Icon,
} as const;

export { MatchesProgress };
